import { Suspense, useLayoutEffect } from "react";
import PrivateRoute from "./PrivateRoute";
import { private_routes, public_routes } from "routes";
import CircleLoader from "components/atoms/CircleLoader";
import { dismissKeyboard } from "utils/navigation.util";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import "./AppRoutes.scss";

const TAB_SCREENS = ["/", "/bookings", "/help", "/settings"];
// import FullScreenLoader from "components/atoms/FullScreenLoader";

/* Where each tab screen was scrolled to, for this session. A native tab bar
   returns you to where you left a tab; here every switch (and every return
   from a booking) started at the top, or slid back into place visibly. */
const tabScroll = new Map<string, number>();

function TabScrollMemory({ path }: { path: string }) {
  useLayoutEffect(() => {
    window.scrollTo(0, tabScroll.get(path) ?? 0);
    const remember = () => tabScroll.set(path, window.scrollY);
    window.addEventListener("scroll", remember, { passive: true });
    return () => window.removeEventListener("scroll", remember);
  }, [path]);
  return null;
}

export default function AppRoutes() {
  const { pathname } = useLocation();
  const isTab = TAB_SCREENS.includes(pathname);

  /* A new screen never inherits the last one's keyboard. A field still
     focused when the screen changed (a tab tapped while typing) kept the
     Android keyboard up, and the next screen was laid out in the half of
     the screen above it: pushed up, the tab bar cut off. */
  useLayoutEffect(() => {
    dismissKeyboard();
  }, [pathname]);

  return (
    <div
      /* Keyed per tab screen so each one fades in on arrival, as a native
         tab switch does, instead of the old screen being swapped out in a
         single frame. Other routes keep one key, so nothing about them
         changes. */
      key={isTab ? pathname : "route"}
      className={isTab ? "yp-tab-screen" : undefined}
      style={{ minHeight: "calc(100vh - 79px)", position: "relative" }}
    >
      {isTab && <TabScrollMemory path={pathname} />}
      <Suspense fallback={<CircleLoader />}>
        <Routes>
          {public_routes.map(({ path, element }, i) => (
            <Route key={i} path={path} element={element} />
          ))}

          {private_routes.map(({ path, element }, i) => (
            <Route
              key={i}
              path={path}
              element={<PrivateRoute>{element}</PrivateRoute>}
            />
          ))}

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </div>
  );
}
